import React, { useEffect, useState, useRef, useCallback } from "react";
import ClipLoader from "react-spinners/ClipLoader";
import { useStateAuthValue } from "../../../../context/Auth/AuthState";
import { useStateProfConsultFilterValue } from "../../../../context/profConsultFilterState";
import { FilterType } from "../../../../utils/FilterUtil";
import {
  evaluateNetworkQuality,
  runPrecallTest,
} from "../../../../services/agoraPrecallService";
import { showToast, ToastType } from "../../../../utils/ToastUtil";
import { useToast } from "../../../../context/Toast/ToastProvider";
import { Button, makeStyles } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  submit: {
    backgroundColor: "#DA2037",
    "&:hover": {
      backgroundColor: "#e84353",
    },
    margin: theme.spacing(3, 0, 2),
    borderRadius: "20px",
    textTransform: "capitalize",
  },
}));

const TestConnection = () => {
  const toastDispatch = useToast();
  const [{ userToken }] = useStateAuthValue();
  const [, dispatch] = useStateProfConsultFilterValue();
  const classes = useStyles();
  
  const [timeoutFinished, setTimeoutFinished] = useState(false);
  const [isLoading, setIsLoadingState] = useState(true);
  const setIsLoading = useCallback((value) => {
    isLoadingRef.current = value;
    setIsLoadingState(value);
  }, []);
  const isLoadingRef = useRef(true);
  const isCancelled = useRef(false);
  const timerRef = useRef(null);

  const goBack = useCallback(() => {
    isCancelled.current = true;
    setIsLoading(false);
    dispatch({
      type: "LOADING",
      clickButton: FilterType.FINISH_CONSULTATION,
      loading: false,
    });
    localStorage.setItem("consult", null);
  }, [dispatch]);

  const handleTestSuccess = useCallback(() => {
    if (!isCancelled.current) {
      setIsLoading(false);
      dispatch({
        type: "LOADING",
        clickButton: FilterType.VIRTUAL_GUARD_CALLED,
        loading: false,
      });
      localStorage.setItem("consult", true);
    }
  }, [dispatch]);

  const handleTestError = useCallback((error) => {
    showToast(
      toastDispatch,
      error.message || "Error al probar la conexión",
      ToastType.ERROR,
      ToastType.ERROR
    );
    setIsLoading(false);
  }, [toastDispatch]);

  useEffect(() => {
    let isMounted = true;
    isCancelled.current = false;
    const abortController = new AbortController();

    const performSpeedTest = async () => {
      try {
        if (!userToken) return;

        // Prueba previa contra Agora, el proveedor de la videollamada
        const readings = await runPrecallTest({ signal: abortController.signal });

        // Verificar si el componente sigue montado y no fue cancelado
        if (!isMounted || isCancelled.current) return;

        if (evaluateNetworkQuality(readings)) {
          handleTestSuccess();
        } else {
          // Si la conexión es inestable, mostrar mensaje
          setTimeoutFinished(true);
          setIsLoading(false);
        }

      } catch (error) {
        if (isMounted && !isCancelled.current) {
          handleTestError(error);
          // No mostrar error de conexión insuficiente si hubo error en la API
          setTimeoutFinished(false);
        }
      }
    };

    // Iniciar prueba
    performSpeedTest();

    // Timeout de seguridad (16 segundos por defecto)
    timerRef.current = setTimeout(() => {
      if (isMounted && !isCancelled.current) {
        // Si el timeout se dispara, verificar si ya hay resultado
        if (isLoadingRef.current) {
          setTimeoutFinished(true);
          setIsLoading(false);
        }
      }
    }, 16000);

    // Cleanup
    return () => {
      isMounted = false;
      abortController.abort();
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [userToken, handleTestSuccess, handleTestError]);

  // Efecto separado para el mensaje de timeout
  useEffect(() => {
    if (timeoutFinished) {
      const returnTimer = setTimeout(() => {
        goBack();
      }, 5000);

      return () => clearTimeout(returnTimer);
    }
  }, [timeoutFinished, goBack]);

  return (
    <div className="test-connection-container">
      <div className="test-connection">
        {timeoutFinished ? (
          <p style={{ textAlign: "center" }} color="grey">
            La calidad de conexión de tu internet es insuficiente para
            establecer una videoconsulta.
          </p>
        ) : (
          <>
            <ClipLoader
              color={"grey"}
              loading={isLoading}
              size={90}
              speedMultiplier={0.6}
              aria-label="Loading Spinner"
              data-testid="loader"
            />
            <p color="grey">Testeando calidad de conexión...</p>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submit}
              onClick={goBack}
            >
              Cancelar
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default TestConnection;